import { STANDE } from "../standeTokens";

/**
 * Where a submission is in its life, in the words a teacher would use. One
 * place for the rule so the filter chips, the rows and the stat strip agree.
 */
export const stageOf = (row) => {
  if (row.submission_status !== "SUBMITTED") return "not_submitted";
  if (row.feedback_released) return "released";
  if (row.manual_marking_status === "COMPLETE") return "marked";
  return "to_review";
};

export const STAGES = {
  not_submitted: { label: "Not submitted", dot: "#9a9a9a" },
  to_review: { label: "To review", dot: STANDE.secondaryOrange },
  marked: { label: "Marked", dot: STANDE.primaryBlue },
  released: { label: "Released", dot: "#2fa360" },
};

export const studentName = (row) =>
  [row.first_name, row.last_name].filter(Boolean).join(" ") || "Unknown student";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatDateTime = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${h}:${String(d.getMinutes()).padStart(2, "0")} ${ampm}`;
};

export const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * Chart fills for the four submission states.
 *
 * The brand blue (#02c9fe) and orange are display colours: at chart size on a
 * pale ground the blue sits at 1.9:1, so it washes out as a fill. These are
 * darker steps of the same hues, checked with the palette validator — every
 * adjacent pair clears the CVD and normal-vision floors and every fill clears
 * 3:1 against its surface, in both modes.
 *
 * Grey is deliberately neutral: "not submitted" is an absence, not a fourth
 * identity. Every chart here also carries a legend and labels, so state is
 * never colour alone.
 */
export const CHART = {
  light: {
    // The brand tints, read as a journey: nothing yet (cream), waiting on you
    // (light red), marked (light green), out to the student (black). Three of
    // the four are paler than the card they sit on, so every block carries a
    // hairline — see BLOCK_EDGE — and the exact counts live in the legend and
    // the tooltip rather than resting on the fill alone.
    not_submitted: STANDE.cream,
    to_review: STANDE.lightRed,
    marked: STANDE.lightGreen,
    released: STANDE.black,
    grid: "#e4e4e4",
    axis: "#9a9a9a",
  },
  dark: {
    // The same relationships inverted: the tints become deep versions of
    // themselves and the ink block becomes the light one.
    not_submitted: "#2b2b2b",
    to_review: "#5c2a24",
    marked: "#24462a",
    released: "#ededed",
    grid: "#2e2e2e",
    axis: "#8a8a8a",
  },
};

/** A pale block on a pale card needs an edge to exist. */
export const BLOCK_EDGE = {
  light: {
    not_submitted: "#e4e0d8",
    to_review: "#f5d5d2",
    marked: "#d5ecd1",
    released: STANDE.black,
  },
  dark: {
    not_submitted: "#3a3a3a",
    to_review: "#76392f",
    marked: "#2f5c37",
    released: "#ededed",
  },
};

export const blockEdge = (isDark) => (isDark ? BLOCK_EDGE.dark : BLOCK_EDGE.light);

/**
 * Inactive columns are ghosted rather than faded. Opacity cannot recede this
 * palette: the ink block stays the heaviest thing on screen at any alpha,
 * and the tints disappear entirely. Swapping the fills for a narrow grey
 * ramp keeps the structure readable while the live column carries the colour.
 */
export const MUTED = {
  light: {
    not_submitted: "#f6f6f6",
    to_review: "#ededed",
    marked: "#e4e4e4",
    released: "#d2d2d2",
    edge: "#e8e8e8",
  },
  dark: {
    not_submitted: "#242424",
    to_review: "#2b2b2b",
    marked: "#333333",
    released: "#414141",
    edge: "#333333",
  },
};

export const mutedColors = (isDark) => (isDark ? MUTED.dark : MUTED.light);

/** Small marks beside text use the same fills the columns do. */
export const DOT_KEY = {
  not_submitted: "not_submitted",
  to_review: "to_review",
  marked: "marked",
  released: "released",
};

export const chartColors = (isDark) => (isDark ? CHART.dark : CHART.light);

/** The order states are stacked in: the work moves left to right. */
export const STATE_ORDER = ["not_submitted", "to_review", "marked", "released"];

export const paperKeyOf = (row) =>
  row.custom_paper_guid || row.past_paper_guid || row.paper_name;

/**
 * One entry per assignment, with its students and the counts each state holds.
 * Everything downstream reads from this, so the overview and the detail can
 * never disagree about a number.
 */
export const groupByAssignment = (rows) => {
  const map = new Map();
  (rows || []).forEach((row) => {
    const key = paperKeyOf(row);
    if (!key) return;
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: row.paper_name || "Assignment",
        kind: row.paper_class || (row.paper_type === "PAST_PAPER" ? "Past paper" : "Paper"),
        dueDate: row.due_date,
        totalMarks: row.total_marks,
        rows: [],
      });
    }
    const entry = map.get(key);
    entry.rows.push(row);
    // The earliest set date is the one the class saw.
    if (!entry.dueDate && row.due_date) entry.dueDate = row.due_date;
  });

  return [...map.values()]
    .map((a) => {
      const counts = { not_submitted: 0, to_review: 0, marked: 0, released: 0 };
      a.rows.forEach((r) => { counts[stageOf(r)] += 1; });
      const scored = a.rows.filter(
        (r) => r.submission_status === "SUBMITTED" && r.total_scored_marks != null && r.total_marks
      );
      const average = scored.length
        ? scored.reduce((n, r) => n + (r.total_scored_marks / r.total_marks) * 100, 0) / scored.length
        : null;
      return {
        ...a,
        counts,
        total: a.rows.length,
        submitted: a.rows.length - counts.not_submitted,
        needsReview: counts.to_review,
        average,
        scoredCount: scored.length,
      };
    })
    .sort((x, y) => {
      // What needs the teacher most comes first.
      if (y.needsReview !== x.needsReview) return y.needsReview - x.needsReview;
      return (y.dueDate ? new Date(y.dueDate) : 0) - (x.dueDate ? new Date(x.dueDate) : 0);
    });
};

/** Percentage score for one attempt, or null when it has not been marked. */
export const pctOf = (row) =>
  row.total_scored_marks != null && row.total_marks
    ? (row.total_scored_marks / row.total_marks) * 100
    : null;

/** Five bands is enough to see the shape of a class without inventing detail. */
export const BANDS = [
  { label: "0–20", min: 0, max: 20 },
  { label: "21–40", min: 20, max: 40 },
  { label: "41–60", min: 40, max: 60 },
  { label: "61–80", min: 60, max: 80 },
  { label: "81–100", min: 80, max: 100.01 },
];

export const bandCounts = (rows) => {
  const counts = BANDS.map(() => 0);
  rows.forEach((r) => {
    const pct = pctOf(r);
    if (pct == null) return;
    const i = BANDS.findIndex((b) => pct >= b.min && pct < b.max);
    if (i >= 0) counts[i] += 1;
  });
  return counts;
};
